import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@gymlog/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Storage key for the avatar (`avatars/{userId}/{hash}.webp`).
   *
   * Placed here and deliberately not read from the session: the session is served by the API
   * and cached, so a freshly uploaded photo would not be reflected until the
   * next refresh. By reading it from the local row — just like language,
   * font, and theme — the photo appears instantly and survives offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * ALWAYS filters by `userId`, just like all other `user` reads in this
 * module. Previously the filter was conditional and guests ended up with
 * a bare `SELECT … FROM user LIMIT 1`: without `WHERE`, SQLite returns whichever FIRST
 * row it encounters, which is not necessarily the guest's. That is not just a
 * correctness bug but a data leak across accounts: on guest→real
 * upgrade, the local DB downloads a `user` row for each co-member of the
 * organization, so the guest ended up displaying SOMEONE ELSE's theme.
 * Without `WHERE` there is no possible empty result: someone's row always comes back.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
